import React from 'react';

import { Button, Group, Modal, ModalProps, Stack, Text, Title } from '@mantine/core';
import KiloAlert from 'components/KiloAlert';
import useForceResyncEvents from 'hooks/googleCalendarIntegration/useForceResyncEvents';
import { GoogleCalendarIntegration } from 'types';
import notificationHandler from 'utils/notifications';

interface ForceResyncModalProps extends Pick<ModalProps, 'opened' | 'onClose'> {
  googleCalendarIntegration: GoogleCalendarIntegration;
  handleResetSyncSettings: () => void;
}

const ForceResyncModal = ({
  opened,
  onClose,
  googleCalendarIntegration,
  handleResetSyncSettings,
}: ForceResyncModalProps): JSX.Element => {
  const { successNotification, errorNotification } = notificationHandler();

  const resyncEventsMutation = useForceResyncEvents(googleCalendarIntegration.googleCalendarIntegrationId);

  const showAppointments = googleCalendarIntegration.syncAppointmentsToCalendar;
  const showClasses = googleCalendarIntegration.syncClassesToCalendar;

  const eventText = [];
  if (showAppointments) eventText.push('appointments');
  if (showClasses) eventText.push('classes');

  const handleSubmit = (): void => {
    resyncEventsMutation.mutate(null, {
      onError: (error: any) => {
        errorNotification('Resync failed', error.response?.data.message);
      },
      onSuccess: () => {
        successNotification('Resync started. Your calendar events are being updated in the background.');
        onClose();
        handleResetSyncSettings();
      },
    });
  };

  return (
    <Modal
      centered
      opened={opened}
      onClose={onClose}
      size='lg'
      title={<Title order={3}>Force Resync</Title>}
    >
      <Stack>
        <Stack px='md'>
          <Text size='lg'>
            This will resync your Google Calendar with Kilo, adding upcoming events using your configuration
            ({eventText.join(' and ')}) and removing any events that should not be there.
          </Text>
          <KiloAlert>It may take some time for your Google Calendar to update</KiloAlert>
        </Stack>

        <Group mt='lg' position='right'>
          <Button disabled={resyncEventsMutation.isLoading} onClick={onClose} variant='outline'>
            Cancel
          </Button>
          <Button onClick={handleSubmit} loading={resyncEventsMutation.isLoading}>
            Confirm
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
};

export default ForceResyncModal;
